import styles from './index.module.css'
import { IS_ARTIST } from '@disco/constants/FeatureTypes'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import React, { useEffect } from 'react'
import { useSelector } from 'hooks/use-redux'
import { useNavigate } from 'react-router-dom'
import { getUser } from '@disco/state-management/selectors/user'
import type { User } from 'types/user'

import artistsAdvance from 'assets/images/artists-advance@2x.jpg'
import oneWorld from 'assets/images/one-world.png'

import AspectRatio from 'components/ui/aspect-ratio'
import Button from 'components/ui/button'
import Stack from 'components/ui/stack'
import { PartnerDiscounts } from './partner-discounts'
import mixpanel from '@disco/api/mixpanel'

const OpportunityImage = ({
  image,
  fillColor,
  fit = 'cover',
}: {
  image: string
  fillColor?: string
  fit?: 'contain' | 'cover' | 'fill' | 'none'
}): React.ReactElement<'div'> => (
  <div className={`${styles.column} ${styles['column--image']}`}>
    <AspectRatio
      image={image}
      fill={fillColor}
      ratio={1}
      shape="none"
      fit={fit}
    />
  </div>
)

const OpportunityDescription = ({
  header,
  body,
  cta,
}): React.ReactElement<'div'> => (
  <div className={styles.column}>
    <div className={styles.row}>
      <h2 className={styles.heading}>{header}</h2>
    </div>
    <div className={styles.row}>{body}</div>
    <div className={styles.row}>{cta}</div>
  </div>
)

const Opportunity = ({
  containerClassName,
  children,
}: {
  containerClassName: string
  children: React.ReactNode
}): React.ReactElement<'div'> => {
  return (
    <div className={`${styles.container} ${containerClassName}`}>
      {children}
    </div>
  )
}

const OpportunityCaption = ({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement<'div'> => (
  <div className={styles['opportunity-caption']}>{children}</div>
)

Opportunity.Image = OpportunityImage
Opportunity.Description = OpportunityDescription
Opportunity.Caption = OpportunityCaption

const ArtistOpportunities = (): React.ReactElement<'div'> => {
  const navigate = useNavigate()
  const isArtist: boolean = useFeatureEnabled(IS_ARTIST)
  const user: User = useSelector(getUser)

  useEffect(() => {
    if (!isArtist) navigate('/', { replace: true })
  }, [isArtist, navigate])
  return (
    <div className="Page">
      <div className="Page_Content">
        <Stack>
          <Opportunity containerClassName={styles['bg-dark']}>
            <Opportunity.Description
              header="Helping artists advance their careers"
              body={
                <>
                  <p className={styles.text}>
                    DISCO is interested in helping artists advance their careers
                    by connecting your music directly to people & opportunities
                    in our ecosystem.
                  </p>
                  <p className={styles.text}>
                    Please take this quick survey to see if your music is
                    eligible for our early access community and let us know what
                    other opportunities you’d like to see on DISCO.
                  </p>
                </>
              }
              cta={
                <Button
                  appearance="primary"
                  caption="Take survey"
                  link={`https://form.typeform.com/to/xOF0KOJT#user_id=${user.id}&business_id=${user.business.id}`}
                  linkTarget="_blank"
                  onClick={() =>
                    mixpanel.sendPureEvent('Click button', {
                      Location: 'Explorer',
                      Context: 'Opportunities',
                      Campaign: 'Helping artists advance their careers',
                      Copy: 'Take survey',
                    })
                  }
                />
              }
            />
            <div className={styles['opportunity-image-container']}>
              <Opportunity.Image image={artistsAdvance} fillColor="#FFFFFF" />
              <Opportunity.Caption>
                Photo Credit: Lily Nevers featuring Jordi Up Late and manny
                moura
              </Opportunity.Caption>
            </div>
          </Opportunity>
          <PartnerDiscounts />
        </Stack>
      </div>
    </div>
  )
}

export default ArtistOpportunities
